import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { environment } from '../../environments/environment';
import { RideAlongService } from './ride-along.service';
import { UserProfileService } from './user-profile.service';
import { CardParticipant, QuickObs, extractQuickObs } from '../shared/utils/vessel-card.logic';

/** Active ride-along context for a vessel (`GET observations/ride-along`). */
export interface RideAlongContext {
  participants: CardParticipant[];
  enabled: boolean;
  tripState: string | null;
  tripName: string | null;
  tripId: string | null;
}

export interface OwnerIdentity {
  name: string | null;
  imageUrl: string | null;
}

/**
 * Data access for the boat card — the network half of Flutter's
 * `_CompactObservationPopup` (`_fetchLatestObsForVessel`, `_fetchRideAlongContext`,
 * `_lookupOwnerIdentity`). No offline write-through cache: the web has no
 * equivalent of Flutter's FeedStorageService and the card shows its own loading state.
 */
@Injectable({ providedIn: 'root' })
export class VesselCardService {
  private readonly apiUrl = environment.apiUrl;

  /** Shared across every card so tapping boat after boat doesn't re-hit the profile API. */
  private readonly ownerIdentityCache = new Map<string, Promise<OwnerIdentity>>();

  constructor(
    private http: HttpClient,
    private rideAlong: RideAlongService,
    private profiles: UserProfileService,
  ) {}

  extract(obs: any): QuickObs {
    return extractQuickObs(obs, (row) => this.rideAlong.mapEmbeddedParticipant(row));
  }

  /** `observations/latest?vessel_id=` — `ride_along=false`: participants come from their own endpoint. */
  loadLatest(vesselId: string): Promise<any> {
    const params = new HttpParams().set('vessel_id', vesselId).set('ride_along', 'false');
    return firstValueFrom(this.http.get(`${this.apiUrl}/observations/latest`, { params }));
  }

  loadById(observationId: string): Promise<any> {
    return firstValueFrom(this.http.get(`${this.apiUrl}/observations/${encodeURIComponent(observationId)}/`));
  }

  /** Null on any failure — enrichment must never break the card. */
  async fetchRideAlongContext(vesselId: string): Promise<RideAlongContext | null> {
    try {
      const params = new HttpParams().set('vessel_id', vesselId);
      const res: any = await firstValueFrom(this.http.get(`${this.apiUrl}/observations/ride-along`, { params }));
      const raw = res?.ride_along_participants;
      return {
        participants: Array.isArray(raw)
          ? raw.filter((p: any) => p && typeof p === 'object').map((p: any) => this.rideAlong.mapEmbeddedParticipant(p))
          : [],
        enabled: res?.ride_along_enabled === true,
        tripState: typeof res?.trip_state === 'string' ? res.trip_state : null,
        tripName: typeof res?.ride_along_trip_name === 'string' ? res.ride_along_trip_name : null,
        tripId: typeof res?.trip_id === 'string' ? res.trip_id : null,
      };
    } catch {
      return null;
    }
  }

  lookupOwnerIdentity(userId: string): Promise<OwnerIdentity> {
    const id = userId.trim();
    if (!id) return Promise.resolve({ name: null, imageUrl: null });
    let cached = this.ownerIdentityCache.get(id);
    if (!cached) {
      cached = firstValueFrom(this.profiles.getProfileById(id))
        .then((res: any) => {
          const pick = (keys: string[]): string | null => {
            for (const k of keys) {
              const v = res?.[k] == null ? '' : String(res[k]).trim();
              if (v && v.toLowerCase() !== 'null') return v;
            }
            return null;
          };
          return {
            name: pick(['name', 'full_name', 'display_name']),
            imageUrl: pick(['image_url', 'imageURL', 'profile_image', 'profile_image_url', 'avatar_url']),
          };
        })
        .catch(() => ({ name: null, imageUrl: null }));
      this.ownerIdentityCache.set(id, cached);
    }
    return cached;
  }
}
